import { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Formik, Form } from 'formik';
import { AttentionTriangleIcon, InfoIcon } from '@rsos/assets/icons';
import { Box, Button, Modal, Text } from '@rsos/base-components';
import InputText from '@rsos/base-components/form/inputs/InputText';
import {
  ModalFooter,
  ModalBody,
  ModalHeader,
} from '@rsos/components/capstone/base/Modal/ModalComponents';
import { SmartPhraseEditor } from '@rsos/components/capstone/base/SmartPhrase';
import { ErrorWrapper } from '@rsos/components/capstone/base/form/ErrorsWrapper';
import {
  useCreatePreconfiguredMessageMutation,
  useUpdatePreconfiguredMessageMutation,
} from '@rsos/reduxDataStore/src/features/preconfiguredMessages';
import type { PreconfiguredResponse } from '@rsos/reduxDataStore/src/features/preconfiguredMessages/preconfiguredMessagesAPI';
import { organizationSelector } from '@rsos/reduxDataStore/src/features/selectors/organizationSelector';
import { notifySuccess } from '@rsos/utils/toastUtils';
import { AppState } from '../../../types';
import {
  trackCreatePreconfiguredResponse,
  trackEditPreconfiguredResponse,
} from '../preconfiguredMessagesTrackings';
import styles from './ManageMessageModal.module.css';
import { InputTextLabel, BEErrorWrapper } from './index.styles';
import {
  validationSchema,
  CONFLICT_ERROR,
  SUCCESS_MESSAGE,
  UPDATE_SUCCESS_MESSAGE,
  EDIT_HEADER,
  CREATE_HEADER,
} from './utils';

interface MessageData {
  title: string;
  body: string;
  id?: string | undefined;
}

interface Props {
  isOpened: boolean;
  onClose: () => void;
  message?: PreconfiguredResponse | null;
  isEdit?: boolean;
}

const TRIGGER_CHAR = '/';

const ManageMessageModal: React.FC<Props> = ({
  isOpened,
  onClose,
  message,
  isEdit,
}: Props) => {
  const [friendlyError, setFriendlyError] = useState('');
  const orgName =
    useSelector((state: AppState) => organizationSelector(state))?.name || '';

  const [createPreconfiguredMessage, { isLoading: isCreating }] =
    useCreatePreconfiguredMessageMutation();
  const [updatePreconfiguredMessage, { isLoading: isUpdating }] =
    useUpdatePreconfiguredMessageMutation();
  const isSubmitting = isCreating || isUpdating;

  const handleClose = useCallback(() => {
    setFriendlyError('');
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(
    async (values: MessageData): Promise<void> => {
      const successMessage = isEdit ? UPDATE_SUCCESS_MESSAGE : SUCCESS_MESSAGE;

      try {
        if (isEdit) {
          trackEditPreconfiguredResponse(message);
          await updatePreconfiguredMessage({
            org: orgName,
            id: message?.id || '',
            updates: values,
          }).unwrap();
        } else {
          trackCreatePreconfiguredResponse(values);
          await createPreconfiguredMessage({
            org: orgName,
            message: values,
          }).unwrap();
        }
        notifySuccess(successMessage);
        handleClose();
      } catch (error: unknown) {
        const err = error as { status?: number; data?: { message?: string } };
        setFriendlyError(
          err.status === 409
            ? CONFLICT_ERROR
            : err.data?.message || 'An error occurred',
        );
      }
    },
    [
      isEdit,
      handleClose,
      message,
      orgName,
      createPreconfiguredMessage,
      updatePreconfiguredMessage,
    ],
  );

  const initialValues: MessageData = {
    title: message?.title || '',
    body: message?.body || '',
  };

  return (
    <Modal
      isOpened={isOpened}
      onClose={handleClose}
      disableBackdropClick
      width="400px"
      id="modal-manage-preconfigured-message"
      data-name="manage-message-modal"
    >
      <ModalHeader onClose={handleClose}>
        <Text size="large" color="primaryText" data-name="modal-header">
          {isEdit ? EDIT_HEADER : CREATE_HEADER}
        </Text>
      </ModalHeader>
      {friendlyError && (
        <BEErrorWrapper>
          <AttentionTriangleIcon width={14} height={14} />
          <Text size="normal">{friendlyError}</Text>
        </BEErrorWrapper>
      )}
      <Formik<MessageData>
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        validateOnBlur={true}
      >
        {({
          values,
          isValid,
          errors,
          handleChange,
          handleBlur,
          touched,
          setFieldValue,
        }) => {
          return (
            <Form id="manage-message" data-name="manage-message-form">
              <ModalBody my={16}>
                <Box flow={32}>
                  <InputText
                    name="title"
                    value={values.title}
                    error={
                      errors.title && touched.title ? errors.title : undefined
                    }
                    onBlur={handleBlur}
                    shouldErrorTakeSpace={false}
                    onChange={handleChange}
                    label="Message Title"
                    shouldDisplayClearButton={false}
                    data-name="message-title-input"
                  />
                  <Box>
                    <InputTextLabel>Message Body</InputTextLabel>
                    <Text
                      size="small"
                      color="secondaryText"
                      mb={2}
                      data-name="smart-phrase-hint"
                    >
                      Type {TRIGGER_CHAR} to insert a smart phrase
                      <span
                        className={styles.infoTooltip}
                        data-tooltip="Smart phrases are reusable text snippets that auto-populate dynamic values when inserted into a message."
                      >
                        <InfoIcon width={12} height={12} />
                      </span>
                    </Text>
                    <SmartPhraseEditor
                      name="body"
                      value={values.body}
                      onBlur={handleBlur}
                      onChange={value => setFieldValue('body', value)}
                      data-name="message-body-input"
                      triggerChar={TRIGGER_CHAR}
                    />
                    {errors.body && touched.body && (
                      <ErrorWrapper error={errors.body} />
                    )}
                  </Box>
                </Box>
              </ModalBody>
              <ModalFooter mt={32}>
                <Box horizontal flow={12} width="100%">
                  <Button
                    btnType="tertiary"
                    type="button"
                    onClick={handleClose}
                    grow
                    noMargin
                    dataName="cancel-button"
                  >
                    Cancel
                  </Button>
                  <Button
                    btnType="primary"
                    grow
                    noMargin
                    type="submit"
                    disabled={!isValid || isSubmitting}
                    isLoadingProp={isSubmitting}
                    dataName="submit-button"
                  >
                    {isEdit ? 'Update' : 'Create'}
                  </Button>
                </Box>
              </ModalFooter>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default ManageMessageModal;
